/**
 * Платежи в Solana через провайдер Reown AppKit (Phantom, Solflare, WalletConnect).
 *
 * SPL-перевод собран руками: @solana/spl-token не в зависимостях, а нужны ровно
 * две инструкции — идемпотентное создание associated token account получателя
 * и transferChecked. Верификация на сервере смотрит на дельты балансов, поэтому
 * форма инструкции значения не имеет.
 */

import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
  LAMPORTS_PER_SOL,
} from '@solana/web3.js';
import { TOKEN_CONTRACTS } from '../constants/networks';

export interface SolanaWalletProvider {
  sendTransaction(transaction: Transaction, connection: Connection, options?: unknown): Promise<string>;
  signAndSendTransaction?(transaction: Transaction, options?: unknown): Promise<string>;
}

export interface SolanaPayParams {
  from: string;
  to: string;
  amount: number;
  currency: 'SOL' | 'USDC' | 'USDT';
}

const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL');

export function getAssociatedTokenAddress(owner: PublicKey, mint: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  )[0];
}

function u64le(value: bigint): Buffer {
  const buf = Buffer.alloc(8);
  let v = value;
  for (let i = 0; i < 8; i++) {
    buf[i] = Number(v & BigInt(0xff));
    v >>= BigInt(8);
  }
  return buf;
}

function toUnits(amount: number, decimals: number): bigint {
  const [int, frac = ''] = amount.toFixed(decimals).split('.');
  return BigInt(int + frac.padEnd(decimals, '0'));
}

function createAtaIdempotentInstruction(payer: PublicKey, ata: PublicKey, owner: PublicKey, mint: PublicKey): TransactionInstruction {
  return new TransactionInstruction({
    programId: ASSOCIATED_TOKEN_PROGRAM_ID,
    keys: [
      { pubkey: payer, isSigner: true, isWritable: true },
      { pubkey: ata, isSigner: false, isWritable: true },
      { pubkey: owner, isSigner: false, isWritable: false },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data: Buffer.from([1]) as any, // CreateIdempotent
  });
}

function transferCheckedInstruction(
  source: PublicKey,
  mint: PublicKey,
  destination: PublicKey,
  owner: PublicKey,
  amount: bigint,
  decimals: number
): TransactionInstruction {
  return new TransactionInstruction({
    programId: TOKEN_PROGRAM_ID,
    keys: [
      { pubkey: source, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: destination, isSigner: false, isWritable: true },
      { pubkey: owner, isSigner: true, isWritable: false },
    ],
    data: Buffer.concat([Buffer.from([12]), u64le(amount), Buffer.from([decimals])] as any) as any, // TransferChecked
  });
}

/** Отправить SOL или USDC на адрес платформы. Возвращает подпись транзакции. */
export async function paySolana(
  provider: SolanaWalletProvider,
  connection: Connection,
  params: SolanaPayParams
): Promise<string> {
  if (!(params.amount > 0)) {
    throw new Error('Invalid amount');
  }
  const from = new PublicKey(params.from);
  const to = new PublicKey(params.to);

  const tx = new Transaction();
  if (params.currency === 'SOL') {
    tx.add(
      SystemProgram.transfer({
        fromPubkey: from,
        toPubkey: to,
        lamports: Number(toUnits(params.amount, 9)),
      })
    );
  } else {
    const token = params.currency === 'USDT' ? TOKEN_CONTRACTS.solana.USDT : TOKEN_CONTRACTS.solana.USDC;
    const mint = new PublicKey(token.address);
    const sourceAta = getAssociatedTokenAddress(from, mint);
    const destAta = getAssociatedTokenAddress(to, mint);
    tx.add(createAtaIdempotentInstruction(from, destAta, to, mint));
    tx.add(transferCheckedInstruction(sourceAta, mint, destAta, from, toUnits(params.amount, token.decimals), token.decimals));
  }

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  tx.recentBlockhash = blockhash;
  tx.lastValidBlockHeight = lastValidBlockHeight;
  tx.feePayer = from;

  try {
    const signature = await provider.sendTransaction(tx, connection);
    if (!signature) {
      throw new Error('Wallet returned no signature');
    }
    return signature;
  } catch (err: any) {
    if (err?.code === 4001 || /reject|denied|cancel/i.test(err?.message || '')) {
      throw new Error('Transaction cancelled by user');
    }
    throw err instanceof Error ? err : new Error(String(err));
  }
}

export { LAMPORTS_PER_SOL };
